// @djsem/analysis-contract — Set Builder + Transition Plans (Prompt 12).
//
// Assemble ordered sets, plan transitions, validate live readiness, and export an IMMUTABLE
// approved set snapshot for Rehearsal / Live Show Conductor. Reuses P07 sorting, P08
// chapters, P09 cues, and the P07A deterministic hash — no duplicate approval/scoring
// systems. Draft sets are never mutated by export (rule 6); live readiness requires every
// live track to carry an approved package snapshot ref (rule 5). Prep/Set-Builder side only.

import {
  type DJSEMChapter,
  SetFlowSortingService, type DJSEMTrackSortInput, type DJSEMTrackSortOptions,
  type DJSEMCuePoint,
  type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult,
} from "@djsem/shared";
import { deterministicHash } from "./prepWorkspace";
import { SeqCounter, deepFreeze } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------
// Canonical DJSEMSetArc, DJSEMTransitionType, DJSEMTransitionPlan live in @djsem/shared
// (showDirection). This file re-exports them for callers that already imported them from
// setBuilder — one shape, no drift (rule 2).
export type { DJSEMSetArc, DJSEMTransitionType, DJSEMTransitionPlan } from "@djsem/shared";
import type { DJSEMSetArc, DJSEMTransitionType, DJSEMTransitionPlan } from "@djsem/shared";

/** A single track's placement within a DJ set (position, chapter/transition assignment, lock state). */
export interface DJSEMSetTrack {
  id: string; preparationTrackId: string; position: number;
  assignedChapterId?: string; transitionInId?: string; transitionOutId?: string;
  notes?: string; locked: boolean; warnings: string[];
}

/** A draft or reviewed DJ set: ordered tracks, chapters, energy arc, and live-readiness state. */
export interface DJSEMSet {
  id: string; title: string; description?: string;
  tracks: DJSEMSetTrack[]; chapters: DJSEMChapter[]; setArc: DJSEMSetArc;
  targetDurationMinutes?: number; createdAt: string; updatedAt: string;
  reviewStatus: "draft" | "needs_review" | "reviewed" | "approved" | "rejected";
  liveReadiness: { approvedForLive: boolean; blockers: string[]; warnings: string[] };
}

/** An immutable, deterministically-hashed approved set snapshot for Rehearsal / Live Show Conductor. */
export interface DJSEMApprovedSetSnapshot {
  snapshotId: string; setId: string; snapshotVersion: string; createdAt: string; createdBy?: string;
  set: DJSEMSet;
  approvedTrackPackageRefs: Array<{ preparationTrackId: string; approvedPackageId: string; approvedSnapshotHash: string }>;
  transitionPlans: DJSEMTransitionPlan[];
  visualSceneRefs: string[]; lightingSceneRefs: string[]; warnings: string[]; hash: string;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link SetBuilderService.createSet}. */
export interface CreateSetInput { id?: string; title: string; description?: string; setArc?: DJSEMSetArc; chapters?: DJSEMChapter[]; targetDurationMinutes?: number; createdAt?: string; }
/** Input for {@link SetBuilderService.addTrackToSet}. */
export interface AddTrackToSetInput { set: DJSEMSet; preparationTrackId: string; position?: number; locked?: boolean; approvedForLive?: boolean; }
/** Input for {@link SetBuilderService.removeTrackFromSet}. */
export interface RemoveTrackFromSetInput { set: DJSEMSet; setTrackId: string; }
/** Input for {@link SetBuilderService.reorderSetTrack}. */
export interface ReorderSetTrackInput { set: DJSEMSet; orderedSetTrackIds: string[]; }
/** Input for {@link SetBuilderService.assignTrackToChapter}. */
export interface AssignTrackToChapterInput { set: DJSEMSet; setTrackId: string; chapterId: string; }
/** Input for {@link SetBuilderService.applySuggestedSort}. */
export interface ApplySuggestedSortInput { set: DJSEMSet; sortInputs: DJSEMTrackSortInput[]; sortOptions: DJSEMTrackSortOptions; }
/** Input for {@link SetBuilderService.createTransitionPlan}. */
export interface CreateTransitionPlanInput { fromTrackId: string; toTrackId: string; transitionType: DJSEMTransitionType; mixOutCueId?: string; mixInCueId?: string; visualSceneId?: string; lightingSceneId?: string; notes?: string; }
/** Input for {@link SetBuilderService.suggestTransitionPlans}. */
export interface SuggestTransitionPlansInput { set: DJSEMSet; cuesByTrackId?: Record<string, DJSEMCuePoint[]>; }
/** Input for {@link SetBuilderService.validateSetReadiness}. */
export interface ValidateSetReadinessInput { set: DJSEMSet; approvedPackageByTrackId?: Record<string, { approvedPackageId: string; approvedSnapshotHash: string }>; mode?: "draft" | "live"; }
/** Input for {@link SetBuilderService.exportApprovedSetSnapshot}. */
export interface ExportApprovedSetSnapshotInput { set: DJSEMSet; approvedPackageByTrackId: Record<string, { approvedPackageId: string; approvedSnapshotHash: string }>; transitionPlans?: DJSEMTransitionPlan[]; createdBy?: string; createdAt?: string; }

/** The result of a set edit operation: the updated set plus any warnings. */
export interface DJSEMSetEditResult { ok: boolean; set: DJSEMSet; warnings: string[] }

// ---- service (class-based; hard rule 12) ------------------------------------

const _seq = new SeqCounter();
const nowSeq = () => _seq.next();

/**
 * Assembles ordered DJ sets, plans transitions, validates live readiness, and exports an
 * immutable approved set snapshot for Rehearsal / Live Show Conductor. Reuses shared
 * sorting/chapters/cues/hashing rather than duplicating approval or scoring logic. Prep-side only.
 */
export class SetBuilderService {
  private sorter = new SetFlowSortingService();

  /**
   * Create a new draft set.
   * @param input - the set's title and optional description/arc/chapters/duration/id.
   * @returns the created draft set.
   */
  createSet(input: CreateSetInput): DJSEMSet {
    const at = input.createdAt ?? "1970-01-01T00:00:00.000Z";
    return {
      id: input.id ?? `set_${input.title.replace(/\s+/g, "_").toLowerCase()}`,
      title: input.title, description: input.description,
      tracks: [], chapters: input.chapters ?? [], setArc: input.setArc ?? "custom",
      targetDurationMinutes: input.targetDurationMinutes, createdAt: at, updatedAt: at,
      reviewStatus: "draft",
      liveReadiness: { approvedForLive: false, blockers: [], warnings: [] },
    };
  }

  /**
   * Add a track to a set at an optional position, warning if it's not yet approved for live.
   * @param input - the set, track id, and optional position/lock/approval flags.
   * @returns the edit result with the track added and positions reindexed.
   */
  addTrackToSet(input: AddTrackToSetInput): DJSEMSetEditResult {
    const warnings: string[] = [];
    if (!input.approvedForLive) warnings.push(`Track ${input.preparationTrackId} is not approved for live (added as draft).`);
    const track: DJSEMSetTrack = {
      id: `st_${input.preparationTrackId}_${nowSeq()}`, preparationTrackId: input.preparationTrackId,
      position: input.position ?? input.set.tracks.length, locked: !!input.locked, warnings,
    };
    const tracks = [...input.set.tracks];
    if (input.position !== undefined && input.position >= 0 && input.position < tracks.length) tracks.splice(input.position, 0, track);
    else tracks.push(track);
    return this.result(input.set, this.reindex(tracks), warnings);
  }

  /**
   * Remove a track from a set, refusing if it's locked.
   * @param input - the set and the set-track id to remove.
   * @returns the edit result; fails if the track is not found or is locked.
   */
  removeTrackFromSet(input: RemoveTrackFromSetInput): DJSEMSetEditResult {
    const track = input.set.tracks.find((t) => t.id === input.setTrackId);
    if (!track) return { ok: false, set: input.set, warnings: ["Set track not found."] };
    if (track.locked) return { ok: false, set: input.set, warnings: [`Track ${input.setTrackId} is locked; unlock before removing.`] };
    return this.result(input.set, this.reindex(input.set.tracks.filter((t) => t.id !== input.setTrackId)), []);
  }

  /**
   * Reorder a set's tracks to a caller-supplied permutation, refusing if any locked track would move.
   * @param input - the set and the desired ordered set-track id list.
   * @returns the edit result; fails if the id list isn't a valid permutation or a locked track would move.
   */
  reorderSetTrack(input: ReorderSetTrackInput): DJSEMSetEditResult {
    const byId = new Map(input.set.tracks.map((t) => [t.id, t]));
    if (input.orderedSetTrackIds.length !== input.set.tracks.length || input.orderedSetTrackIds.some((id) => !byId.has(id)))
      return { ok: false, set: input.set, warnings: ["Reorder list must be a permutation of set track ids."] };
    // locked tracks must keep their position
    const warnings: string[] = [];
    const reordered = input.orderedSetTrackIds.map((id) => byId.get(id)!);
    for (const t of input.set.tracks) if (t.locked) {
      const oldPos = t.position, newPos = reordered.findIndex((x) => x.id === t.id);
      if (oldPos !== newPos) warnings.push(`Locked track ${t.id} cannot move; reorder ignored for it.`);
    }
    if (warnings.length) return { ok: false, set: input.set, warnings };
    return this.result(input.set, this.reindex(reordered), []);
  }

  /**
   * Assign a set track to a chapter defined on the set.
   * @param input - the set, set-track id, and chapter id.
   * @returns the edit result; fails if the chapter or track is not found.
   */
  assignTrackToChapter(input: AssignTrackToChapterInput): DJSEMSetEditResult {
    if (!input.set.chapters.some((c) => c.id === input.chapterId)) return { ok: false, set: input.set, warnings: [`Chapter ${input.chapterId} not in set.`] };
    const tracks = input.set.tracks.map((t) => (t.id === input.setTrackId ? { ...t, assignedChapterId: input.chapterId } : t));
    if (!tracks.some((t) => t.id === input.setTrackId)) return { ok: false, set: input.set, warnings: ["Set track not found."] };
    return this.result(input.set, tracks, []);
  }

  /**
   * Apply a suggested track sort, keeping locked tracks pinned to their current positions.
   * @param input - the set, sort inputs, and sort options.
   * @returns the edit result with unlocked tracks reordered per the suggestion.
   */
  applySuggestedSort(input: ApplySuggestedSortInput): DJSEMSetEditResult {
    const sorted = this.sorter.sortTracks(input.sortInputs, input.sortOptions);
    const order = sorted.items.map((i) => i.preparationTrackId);
    // locked tracks keep positions; unlocked tracks fill remaining slots in suggested order
    const locked = input.set.tracks.filter((t) => t.locked);
    const lockedPos = new Set(locked.map((t) => t.position));
    const unlockedInOrder = order
      .map((pid) => input.set.tracks.find((t) => t.preparationTrackId === pid && !t.locked))
      .filter((t): t is DJSEMSetTrack => !!t);
    // Regression: if `sortInputs` omits any currently-unlocked set track (e.g. a caller only
    // sorts a filtered subset), unlockedInOrder used to end up shorter than the number of
    // unlocked positions, and the fill loop below silently dropped the corresponding tracks
    // from the resulting set entirely — no warning, no ok:false, just fewer tracks than before.
    // Append anything missing (in its original relative order) instead of losing it, and warn.
    const coveredIds = new Set(unlockedInOrder.map((t) => t.id));
    const missing = input.set.tracks.filter((t) => !t.locked && !coveredIds.has(t.id));
    const extraWarnings: string[] = [];
    if (missing.length > 0) {
      unlockedInOrder.push(...missing);
      extraWarnings.push(`${missing.length} unlocked track(s) not covered by sortInputs were appended in original order rather than dropped: ${missing.map((t) => t.id).join(", ")}.`);
    }
    const out: DJSEMSetTrack[] = [];
    let ui = 0;
    for (let pos = 0; pos < input.set.tracks.length; pos++) {
      if (lockedPos.has(pos)) out.push(locked.find((t) => t.position === pos)!);
      else if (ui < unlockedInOrder.length) out.push(unlockedInOrder[ui++]);
    }
    return this.result(input.set, this.reindex(out), [...sorted.warnings, ...extraWarnings]);
  }

  /**
   * Manually author a transition plan between two tracks.
   * @param input - the from/to track ids, transition type, and optional cue/scene refs and notes.
   * @returns the constructed transition plan (source "manual", confidence 1).
   */
  createTransitionPlan(input: CreateTransitionPlanInput): DJSEMTransitionPlan {
    return {
      id: `tp_${input.fromTrackId}__${input.toTrackId}`, fromTrackId: input.fromTrackId, toTrackId: input.toTrackId,
      transitionType: input.transitionType, mixOutCueId: input.mixOutCueId, mixInCueId: input.mixInCueId,
      visualSceneId: input.visualSceneId, lightingSceneId: input.lightingSceneId, notes: input.notes,
      confidence: 1, source: "manual", reasons: ["Manually authored transition."], warnings: [],
    };
  }

  /**
   * Suggest transition plans between consecutive tracks based on their cue points (mix-out/in alignment, drop cues).
   * @param input - the set and optional cues keyed by preparation track id.
   * @returns the suggested transition plans (estimated/low-confidence when cues don't align).
   */
  suggestTransitionPlans(input: SuggestTransitionPlansInput): DJSEMTransitionPlan[] {
    const plans: DJSEMTransitionPlan[] = [];
    const tracks = [...input.set.tracks].sort((a, b) => a.position - b.position);
    for (let i = 0; i + 1 < tracks.length; i++) {
      const from = tracks[i], to = tracks[i + 1];
      const fromCues = input.cuesByTrackId?.[from.preparationTrackId] ?? [];
      const toCues = input.cuesByTrackId?.[to.preparationTrackId] ?? [];
      const mixOut = fromCues.find((c) => c.intent === "mix_out") ?? fromCues.find((c) => c.intent === "breakdown");
      const mixIn = toCues.find((c) => c.intent === "mix_in") ?? toCues.find((c) => c.intent === "intro_start");
      const reasons: string[] = []; const warnings: string[] = [];
      let type: DJSEMTransitionType = "blend"; let confidence = 0.4; let source: DJSEMTransitionPlan["source"] = "estimated";
      if (mixOut && mixIn) { reasons.push("Aligned mix-out and mix-in cues."); confidence = 0.75; source = "analysis"; }
      else { warnings.push("No aligned mix cues; transition is an estimate — review."); }
      if (toCues.some((c) => c.intent === "drop")) { type = "drop_switch"; reasons.push("Target has a drop cue."); }
      plans.push({
        id: `tp_${from.preparationTrackId}__${to.preparationTrackId}`, fromTrackId: from.id, toTrackId: to.id,
        transitionType: type, mixOutCueId: mixOut?.id, mixInCueId: mixIn?.id,
        confidence, source, reasons, warnings,
      });
    }
    return plans;
  }

  /**
   * Validate a set's readiness (non-empty, and in "live" mode every track has an approved package snapshot ref).
   * @param input - the set, optional approved-package refs by track, and mode ("draft" or "live").
   * @returns validation result.
   */
  validateSetReadiness(input: ValidateSetReadinessInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    const mode = input.mode ?? "live";
    if (input.set.tracks.length === 0) issues.push({ path: "$.tracks", message: "set has no tracks" });
    for (const t of input.set.tracks) {
      const ref = input.approvedPackageByTrackId?.[t.preparationTrackId];
      if (!ref) {
        const msg = `Track ${t.preparationTrackId} has no approved package snapshot.`;
        if (mode === "live") issues.push({ path: `$.tracks.${t.id}`, message: msg });
      }
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "set_readiness");
  }

  /**
   * Export an immutable, deterministically-hashed approved set snapshot. Never mutates the draft set.
   * @param input - the set, approved-package refs by track, optional transition plans, and creator/timestamp.
   * @returns the frozen approved set snapshot.
   * @throws if the set fails live-readiness validation.
   */
  exportApprovedSetSnapshot(input: ExportApprovedSetSnapshotInput): DJSEMApprovedSetSnapshot {
    const readiness = this.validateSetReadiness({ set: input.set, approvedPackageByTrackId: input.approvedPackageByTrackId, mode: "live" });
    if (!readiness.ok) throw new Error(`Cannot export approved set snapshot: ${readiness.issues.map((i) => i.message).join("; ")}`);

    // Deep clone so the draft set is never mutated + the snapshot is frozen.
    const frozenSet: DJSEMSet = JSON.parse(JSON.stringify({ ...input.set, reviewStatus: "approved", liveReadiness: { approvedForLive: true, blockers: [], warnings: [] } }));
    const refs = input.set.tracks.map((t) => ({
      preparationTrackId: t.preparationTrackId,
      approvedPackageId: input.approvedPackageByTrackId[t.preparationTrackId].approvedPackageId,
      approvedSnapshotHash: input.approvedPackageByTrackId[t.preparationTrackId].approvedSnapshotHash,
    }));
    const transitionPlans = input.transitionPlans ?? [];
    const body = { set: frozenSet, refs, transitionPlans };
    const hash = deterministicHash(body);
    // Preventive, not just detective: this was deep-cloned but never actually Object.freeze'd
    // despite the "the snapshot is frozen" comment above — a stray mutation anywhere downstream
    // would previously succeed silently instead of throwing/no-op'ing.
    return deepFreeze({
      snapshotId: `setsnap_${input.set.id}_${hash}`,
      setId: input.set.id, snapshotVersion: "1.0",
      createdAt: input.createdAt ?? frozenSet.updatedAt, createdBy: input.createdBy,
      set: frozenSet, approvedTrackPackageRefs: refs, transitionPlans,
      visualSceneRefs: dedupe(frozenSet.chapters.flatMap((c) => c.visualSceneIds ?? [])),
      lightingSceneRefs: dedupe(frozenSet.chapters.flatMap((c) => c.lightingSceneIds ?? [])),
      warnings: [], hash,
    });
  }

  // ---- private --------------------------------------------------------------
  private reindex(tracks: DJSEMSetTrack[]): DJSEMSetTrack[] { return tracks.map((t, i) => ({ ...t, position: i })); }
  private result(set: DJSEMSet, tracks: DJSEMSetTrack[], warnings: string[]): DJSEMSetEditResult {
    return { ok: true, set: { ...set, tracks, updatedAt: set.updatedAt }, warnings };
  }
}

function dedupe<T>(xs: T[]): T[] { return [...new Set(xs)]; }
